"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks/shared";
import { MediaApi } from "@/openapi/generated";
import type { MediaGroup } from "@/openapi/generated/models";
import { createAuthenticatedApi } from "@/lib/utils";
import { invalidateAllTeacherMediaQueries } from "@/lib/utils/media/invalidate-teacher-media-queries";
import {
  filterMediaItemsForOwner,
  mediaListContainsId,
} from "@/lib/utils/media/fetch-media-asset";
import { invalidateMediaAssetQueries } from "@/lib/utils/media/media-asset-query-key";
import type { MediaOwnerType } from "./useMediaAssetGet";

export const useMediaAssetRemove = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (request: {
      tenantId: number;
      ownerType: MediaOwnerType;
      ownerId: number;
      mediaGroup: MediaGroup;
      mediaId: number;
    }) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(MediaApi, accessToken);

      if (request.ownerType === "Teacher") {
        const mediaList = await service.apiMediaTypeIdGet({
          type: request.ownerType as never,
          id: request.ownerId,
          xTenantID: Number(request.tenantId),
        });
        const ownerItems = filterMediaItemsForOwner(
          mediaList || [],
          request.ownerType,
          request.ownerId,
        );

        if (!mediaListContainsId(ownerItems, request.mediaId)) {
          throw new Error("This image does not belong to this teacher.");
        }
      }

      await service.apiMediaIdDelete({
        id: request.mediaId,
        xTenantID: Number(request.tenantId),
      });
    },
    onSuccess: async (_, variables) => {
      if (variables.ownerType === "Teacher") {
        await invalidateAllTeacherMediaQueries(queryClient, variables.tenantId);
      } else {
        await invalidateMediaAssetQueries(
          queryClient,
          variables.tenantId,
          variables.ownerType,
          variables.ownerId,
          variables.mediaGroup,
        );
      }

      toast({
        title: "File removed",
        description: "Removed successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to remove file";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutateAsync, isPending, isError, isSuccess };
};

